import React, { useEffect, useState } from 'react';
import { Skeleton } from 'primereact/skeleton';
import moment from 'moment';

import PFCalendar from '../../shared/PFPrime/PFCalendar';
import PFInputText from '../../shared/PFPrime/PFInputText';
import PFMultiSelect from '../../shared/PFPrime/PFMultiSelect';
import { convertDateToLocaleString } from '../../../utils/Helpers';
import { getDataByCube } from '../Widget/service/widget.service';

import { getDimensionDisplayLabel } from './helpers/dashboard-filters.helper';

const mapMasterOptions = response => {
  if (!Array.isArray(response) || !response.length) {
    return [];
  }

  return response.map(item => {
    if (item && typeof item === 'object') {
      return {
        label: item.label ?? item.value ?? item.name,
        value: item.value ?? item.key ?? item.name,
      };
    }

    return {
      label: String(item),
      value: item,
    };
  });
};

const normalizeSelectedValues = value => {
  if (Array.isArray(value)) {
    return value;
  }

  if (value === undefined || value === null || value === '') {
    return [];
  }

  return [value];
};

const formatStoredDate = value => {
  if (!value) {
    return '';
  }

  return moment(value).format('YYYY-MM-DD');
};

const toCalendarRange = value => {
  if (!Array.isArray(value) || !value.length) {
    return null;
  }

  return [
    value[0] ? new Date(convertDateToLocaleString(value[0])) : null,
    value[1] ? new Date(convertDateToLocaleString(value[1])) : null,
  ];
};

const toStoredDateRange = calendarValue => {
  if (!Array.isArray(calendarValue)) {
    return [];
  }

  return [
    formatStoredDate(calendarValue[0]),
    formatStoredDate(calendarValue[1]),
  ];
};

const DashboardFilterField = ({ dimension, value = '', onChange }) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);

  const fieldLabel = getDimensionDisplayLabel(dimension);
  const fieldId = `dashboard-filter-${String(dimension?.name || '')
    .replace(/\./g, '-')
    .replace(/\s+/g, '-')}`;

  useEffect(() => {
    let isCancelled = false;

    if (dimension?.type === 'time') {
      setOptions([]);
      setLoading(false);
      return;
    }

    const fetchFieldOptions = async () => {
      const dimensionName = dimension?.name;
      if (!dimensionName) {
        setOptions([]);
        setLoading(false);
        return;
      }

      const [cubeName, cubeDimension] = dimensionName.split('.');
      if (!cubeName || !cubeDimension) {
        setOptions([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const response = await getDataByCube(cubeName, cubeDimension);
        if (!isCancelled) {
          setOptions(mapMasterOptions(response));
        }
      } catch {
        if (!isCancelled) {
          setOptions([]);
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    fetchFieldOptions();

    return () => {
      isCancelled = true;
    };
  }, [dimension?.name, dimension?.type]);

  if (dimension?.type === 'time') {
    return (
      <span className="p-float-label">
        <PFCalendar
          id={fieldId}
          inputId={fieldId}
          name={dimension?.name}
          value={toCalendarRange(value)}
          selectionMode="range"
          readOnlyInput
          hideOnRangeSelection
          showButtonBar
          appendTo={null}
          onChange={event =>
            onChange?.(
              dimension?.name,
              toStoredDateRange(event?.value ?? event?.target?.value)
            )
          }
        />
        <label htmlFor={fieldId}>{fieldLabel}</label>
      </span>
    );
  }

  if (loading) {
    return <Skeleton height="38px" className="w-full" />;
  }

  if (options.length) {
    return (
      <span className="p-float-label">
        <PFMultiSelect
          inputId={fieldId}
          name={dimension?.name}
          value={normalizeSelectedValues(value)}
          options={options}
          optionLabel="label"
          optionValue="value"
          filter
          showClear
          showSelectAll
          display="chip"
          maxSelectedLabels={2}
          className="w-full"
          onChange={event => onChange?.(dimension?.name, event?.value || [])}
        />
        <label htmlFor={fieldId}>{fieldLabel}</label>
      </span>
    );
  }

  return (
    <span className="p-float-label">
      <PFInputText
        id={fieldId}
        type="text"
        value={Array.isArray(value) ? '' : value || ''}
        onChange={event =>
          onChange?.(dimension?.name, event?.target?.value || '')
        }
      />
      <label htmlFor={fieldId}>{fieldLabel}</label>
    </span>
  );
};

export default DashboardFilterField;
